import { useEffect, useRef, useCallback } from "react";
import { refreshAccessToken, isTokenExpired } from "../config/tokenRefreshService";

// Refresh interval: 4 dakika (access token 5 dk olduğu için 1 dk önce yenile)
const REFRESH_INTERVAL_MS = 4 * 60 * 1000;

/**
 * Token yenileme hook'u
 * - Periyodik olarak access token'ı yeniler
 * - Sayfa arka plandan döndüğünde token kontrolü yapar ve interval'ı sıfırlar
 * - Tüm refresh mantığı tokenRefreshService'de merkezi olarak yönetilir
 */
export const useTokenRefresh = () => {
  const intervalRef = useRef(null);

  // Token'ı yenile
  const refreshTokens = useCallback(async () => {
    const authToken = localStorage.getItem("authToken");
    const refreshToken = localStorage.getItem("refreshToken");

    if (!refreshToken) return false;

    // Access token hala geçerliyse (60 sn+ ömrü varsa) refresh yapma
    if (authToken && !isTokenExpired(authToken, 60)) {
      return true;
    }

    const newToken = await refreshAccessToken();
    if (!newToken) {
      // Tamamen başarısız — login'e yönlendir
      localStorage.removeItem("authToken");
      localStorage.removeItem("refreshToken");
      window.location.href = "/login";
      return false;
    }
    return true;
  }, []);

  // Interval'ı (yeniden) başlat
  const startRefreshInterval = useCallback(() => {
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
    }
    intervalRef.current = setInterval(() => {
      refreshTokens();
    }, REFRESH_INTERVAL_MS);
  }, [refreshTokens]);

  // Interval'ı durdur
  const stopRefreshInterval = useCallback(() => {
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
      intervalRef.current = null;
    }
  }, []);

  // Component mount olduğunda interval'ı başlat
  useEffect(() => {
    const authToken = localStorage.getItem("authToken");
    const refreshToken = localStorage.getItem("refreshToken");

    if (authToken && refreshToken) {
      startRefreshInterval();
    }

    return () => {
      stopRefreshInterval();
    };
  }, [startRefreshInterval, stopRefreshInterval]);

  // Visibility change — sayfa tekrar görünür olduğunda:
  // 1. Token kontrolü yap (uzun süre arka planda kalmış olabilir)
  // 2. Interval'ı sıfırla (tarayıcı throttle etmiş olabilir)
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.visibilityState === "visible") {
        const authToken = localStorage.getItem("authToken");
        const refreshToken = localStorage.getItem("refreshToken");

        if (authToken && refreshToken) {
          // Hemen refresh dene
          refreshTokens();
          // Interval'ı sıfırla — throttle'dan kurtul
          startRefreshInterval();
        }
      }
    };

    document.addEventListener("visibilitychange", handleVisibilityChange);
    return () => {
      document.removeEventListener("visibilitychange", handleVisibilityChange);
    };
  }, [refreshTokens, startRefreshInterval]);

  return {
    refreshTokens,
    startRefreshInterval,
    stopRefreshInterval,
  };
};

export default useTokenRefresh;
